// Highlights in the app: Play of the Game on the results screen, and the player's own highlights,
// watched in 3D with the replay player. The featured player can share a link or save a video.
import { useEffect, useRef, useState } from 'preact/hooks';
import { Clapperboard, Copy, Download, Eye, Link2, Share2, Video } from 'lucide-preact';
import type { ClipRequest, PlayerHighlight, ReplayCameraMode } from '@vm/shared';
import { api } from '../api/routes';
import { errorMessage } from '../api/http';
import { mapName } from '../state/content';
import { openModal } from '../state/nav';
import { isOffline, me, toast } from '../state/store';
import { track } from '../state/telemetry';
import { timeAgo } from '../ui/format';
import { useAsync } from '../ui/hooks';
import { Modal, Spinner } from '../ui/kit';
import type { ReplayView, ReplayViewState } from '../../replay/view';

/** Video needs WebCodecs (the replay player itself loads lazily, with the game). */
const canEncodeVideo = (): boolean => typeof VideoEncoder !== 'undefined' && typeof VideoFrame !== 'undefined';

const CAMERAS: { mode: ReplayCameraMode; label: string }[] = [
  { mode: 'chase', label: 'Follow' },
  { mode: 'pov', label: 'Eyes' },
  { mode: 'orbit', label: 'Wide' },
  { mode: 'drone', label: 'Drone' },
];

/** Polls the match's highlights for a while after it ends (the last ones upload a few seconds late). */
export function PlayOfTheGame(props: { matchId: string }) {
  const [h, setH] = useState<PlayerHighlight | null>(null);
  useEffect(() => {
    let stopped = false;
    let tries = 0;
    const poll = async (): Promise<void> => {
      if (stopped) return;
      try {
        const res = await api.highlights.ofMatch(props.matchId);
        if (stopped) return;
        if (res.highlights.length) {
          setH(res.highlights[0]);
          track('potg_shown', { kind: res.highlights[0].kind, mine: res.highlights[0].mine });
          return;
        }
      } catch {
        /* try again */
      }
      if (++tries < 8) setTimeout(poll, 3000);
    };
    const t = setTimeout(poll, 2500);
    return () => {
      stopped = true;
      clearTimeout(t);
    };
  }, [props.matchId]);
  if (!h) return null;
  return (
    <button class="res-potg" onClick={() => openHighlight(h, 'results')}>
      <Clapperboard size={26} />
      <span class="res-potg-text">
        <b>{h.mine ? 'You made the Play of the Game!' : 'Play of the Game'}</b>
        <span>
          {h.label}
          {!h.mine && h.subjectName ? ` by ${h.subjectName}` : ''}
        </span>
      </span>
      <span class="btn sm primary">Watch</span>
    </button>
  );
}

/** The player's own highlights (profile), kept for 60 days. */
export function MyHighlights() {
  const res = useAsync(async () => (await api.highlights.mine()).highlights, [], !isOffline.value);
  const list = res.data ?? [];
  if (isOffline.value || res.error || res.loading || !list.length) return null;
  return (
    <div class="recent">
      <h4 class="section-title">Your highlights</h4>
      <div class="recent-list">
        {list.map((h) => (
          <button key={h.id} class="recent-row is-highlight" onClick={() => openHighlight(h, 'profile')}>
            <span class="recent-mode">
              <Clapperboard size={18} />
            </span>
            <span class="recent-text">
              <b>{h.label}</b>
              <small>
                {mapName(h.mapId)} · {timeAgo(h.createdAt)}
              </small>
            </span>
            <span class="btn sm primary">Watch</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function openHighlight(h: PlayerHighlight, from: string): void {
  track('highlight_open', { kind: h.kind, mine: h.mine, from });
  openModal((close) => <HighlightModal h={h} close={close} />);
}

function HighlightModal(props: { h: PlayerHighlight; close: () => void }) {
  const stage = useRef<HTMLDivElement>(null);
  const view = useRef<ReplayView | null>(null);
  const [st, setSt] = useState<ReplayViewState | null>(null);
  const [h, setH] = useState(props.h);
  const [busy, setBusy] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [file, setFile] = useState<{ url: string; blob: Blob; name: string } | null>(null);
  const canShare = h.mine && !me.value?.restricted;

  useEffect(() => {
    let v: ReplayView | null = null;
    let stopped = false;
    void (async () => {
      const [{ ReplayView }, gz] = await Promise.all([import('../../replay/view'), api.highlights.replay(h.id)]);
      if (stopped || !stage.current) return;
      v = new ReplayView(stage.current, setSt);
      view.current = v;
      await v.load(gz);
    })().catch((e) => setSt({ loading: false, playing: false, time: 0, start: 0, duration: 0, camera: 'chase', error: errorMessage(e) }));
    return () => {
      stopped = true;
      v?.dispose();
    };
  }, []);
  useEffect(() => () => file && URL.revokeObjectURL(file.url), [file]);

  /** A public link (with a still for link previews, made once). */
  const makeLink = async (): Promise<void> => {
    setBusy('Making your link…');
    try {
      const shared = await api.highlights.share(h.id);
      if (!shared.hasPoster && view.current) {
        try {
          await api.highlights.poster(h.id, await view.current.poster(1200, 630));
        } catch (e) {
          // The link works without a picture.
          console.warn('poster failed', e);
        }
      }
      setH(shared);
      track('highlight_link', { kind: h.kind });
    } catch (e) {
      toast('Could not make a link', { kind: 'error', body: errorMessage(e) });
    } finally {
      setBusy(null);
    }
  };

  const shareLink = async (): Promise<void> => {
    const url = h.shareUrl!;
    if (navigator.share) {
      try {
        await navigator.share({ title: `${h.label} in Voxel Marines`, url });
        track('highlight_share', { kind: h.kind, via: 'sheet' });
        return;
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast('Link copied', { kind: 'success' });
      track('highlight_share', { kind: h.kind, via: 'copy' });
    } catch {
      toast('Copy this link', { body: url });
    }
  };

  /** A vertical clip with the logo and slow motion at the big moment, made on this device. */
  const saveVideo = async (): Promise<void> => {
    const v = view.current;
    const p = v?.player;
    if (!v || !p) return;
    const video = canEncodeVideo();
    const from = p.momentStart;
    const clip: ClipRequest = video
      ? { format: 'mp4', width: 720, height: 1280, fps: 30, from, to: p.duration, camera: st?.camera ?? 'chase', speed: 'moment', brand: true }
      : { format: 'gif', width: 270, height: 480, fps: 15, from, to: Math.min(p.duration, from + 9), camera: st?.camera ?? 'chase', brand: true };
    setBusy(video ? 'Making your video…' : 'Making your GIF…');
    setProgress(0);
    try {
      const blob = await v.exportClip(clip, (d, t) => setProgress(d / t));
      const name = `voxel-marines-${h.kind}.${clip.format}`;
      setFile({ url: URL.createObjectURL(blob), blob, name });
      track('highlight_video', { kind: h.kind, format: clip.format });
    } catch (e) {
      if ((e as Error).message !== 'Export cancelled') toast('Could not make the clip', { kind: 'error', body: errorMessage(e) });
    } finally {
      setBusy(null);
      setProgress(null);
    }
  };

  const shareFile = async (): Promise<void> => {
    if (!file) return;
    const f = new File([file.blob], file.name, { type: file.blob.type });
    if (navigator.canShare?.({ files: [f] })) {
      try {
        await navigator.share({ files: [f], title: 'Voxel Marines' });
        return;
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
      }
    }
    const a = document.createElement('a');
    a.href = file.url;
    a.download = file.name;
    a.click();
  };

  return (
    <Modal title={h.label} icon={<Clapperboard size={22} />} onClose={props.close} size="lg" class="hl-modal">
      <div class="hl-stage-wrap">
        <div class="hl-stage" ref={stage} />
        {(!st || st.loading || busy) && (
          <div class="hl-overlay">
            <Spinner label={busy ?? 'Loading the replay…'} />
            {progress !== null && (
              <div class="hl-progress">
                <span style={{ width: `${Math.round(progress * 100)}%` }} />
              </div>
            )}
          </div>
        )}
        {st?.error && (
          <div class="hl-overlay">
            <p>The replay could not be played: {st.error}</p>
          </div>
        )}
      </div>
      <div class="hl-cams" role="group" aria-label="Camera">
        {CAMERAS.filter((c) => h.subjectName || (c.mode !== 'chase' && c.mode !== 'pov')).map((c) => (
          <button key={c.mode} class={`btn sm ${st?.camera === c.mode ? 'primary' : ''}`} aria-pressed={st?.camera === c.mode} onClick={() => view.current?.setCamera(c.mode)}>
            {c.mode === 'pov' ? <Eye size={16} /> : c.mode === 'drone' ? <Video size={16} /> : null} {c.label}
          </button>
        ))}
      </div>
      <p class="muted hl-note">{h.mine ? 'Tap the replay to pause. Names never show in replays.' : `Tap the replay to pause.${h.subjectName ? ` Featuring ${h.subjectName}.` : ''}`}</p>
      {canShare && (
        <div class="hl-actions">
          {h.shareUrl ? (
            <>
              <button class="btn primary" onClick={() => void shareLink()}>
                <Share2 size={18} /> Share link
              </button>
              <button
                class="btn"
                onClick={() =>
                  void navigator.clipboard
                    .writeText(h.shareUrl!)
                    .then(() => toast('Link copied', { kind: 'success' }))
                    .catch(() => toast('Copy this link', { body: h.shareUrl! }))
                }
              >
                <Copy size={18} /> Copy
              </button>
            </>
          ) : (
            <button class="btn primary" disabled={!!busy || !st || st.loading} onClick={() => void makeLink()}>
              <Link2 size={18} /> Get a share link
            </button>
          )}
          {file ? (
            <button class="btn green" onClick={() => void shareFile()}>
              <Download size={18} /> Save {file.blob.type === 'video/mp4' ? 'video' : 'GIF'}
            </button>
          ) : (
            <button class="btn" disabled={!!busy || !st || st.loading} onClick={() => void saveVideo()}>
              <Video size={18} /> Make a video
            </button>
          )}
          {busy && progress !== null && (
            <button class="btn sm" onClick={() => view.current?.cancelExport()}>
              Cancel
            </button>
          )}
        </div>
      )}
      {canShare && h.shareUrl && <p class="muted hl-note">Anyone with the link can watch it (no names shown) for 60 days.</p>}
    </Modal>
  );
}
